import { GymGlyph, Icon, TabBar, TabBarFab, TabBarItem } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { listBusinesses } from "../../api/auth";
import { useInboxAccess, useInboxUnread } from "../../features/inbox/hooks";
import { useAppContext, useSessionSnapshot } from "../permissions";
import { useServices } from "../services";
import { strings } from "../strings";
import { BellSheet } from "./BellSheet";
import { BranchSheet } from "./BranchSheet";
import { ChromeProvider } from "./chrome";
import { FabSheet } from "./FabSheet";
import { SearchScreen } from "./SearchScreen";
import { mobile } from "./strings";
import s from "./mobile.module.css";

/**
 * The phone shell (D56), below 768px: 52px top bar (switcher, search, bell), 58px tab bar with the centre +, and the sheets
 * they open. Same URL paths as desktop. A thread is full screen and hides both bars.
 */
export function MobileShell() {
  const { client, session } = useServices();
  const state = useSessionSnapshot();
  const ctx = useAppContext();
  const { canView: canInbox } = useInboxAccess();
  const unread = useInboxUnread();
  const location = useLocation();
  const [chrome, setChrome] = useState<{ title: string | null; under: boolean }>({ title: null, under: false });
  const [fab, setFab] = useState(false);
  const [branch, setBranch] = useState(false);
  const [bell, setBell] = useState(false);
  const [search, setSearch] = useState<false | "all" | "members">(false);

  // As on desktop: after a reload the picker is fetched once so the switcher has its branches.
  const picker = useQuery({ queryKey: ["auth", "businesses", state.businessId], queryFn: () => listBusinesses(client), enabled: state.picker.length === 0, staleTime: 5 * 60_000 });
  useEffect(() => { if (picker.data && state.picker.length === 0) session.setPicker(picker.data); }, [picker.data, state.picker.length, session]);
  useEffect(() => { window.scrollTo?.(0, 0); }, [location.pathname]);

  const report = useCallback((title: string | null, under: boolean) => setChrome((c) => (c.title === title && c.under === under ? c : { title, under })), []);

  const path = location.pathname;
  const onThread = /^\/inbox\/[^/]+/.test(path);
  const solid = chrome.title === null || chrome.under;
  const entry = state.picker.find((e) => e.businessId === state.businessId);
  const branchName = state.branchFilter === "all" ? strings.shell.allBranches : (entry?.staff?.branches.find((b) => b.id === state.branchFilter)?.name ?? ctx.me?.branch.name ?? "");
  const gymName = entry?.name ?? ctx.me?.business.name ?? strings.appName;

  const canMembers = ctx.has("members.view");
  const canLeads = ctx.has("leads.view");
  const slot = canMembers ? { to: "/members", label: mobile.tabs.members, icon: "users" as const } : canLeads ? { to: "/leads", label: mobile.tabs.leads, icon: "trial" as const } : null;
  const starts = (p: string) => path === p || path.startsWith(`${p}/`);
  const mainPaths = ["/home", "/inbox", "/members", "/leads"];
  const moreActive = starts("/more") || !mainPaths.some(starts);

  return (
    <ChromeProvider report={report}>
      <div className={s.shell} data-testid="mobile-shell">
        <a className={s.skip} href="#content">{strings.shell.skip}</a>
        {onThread ? null : (
          <header className={s.bar} data-solid={solid || undefined}>
            <button type="button" className={s.gsw} aria-label={mobile.top.switchBranch(`${gymName}${branchName ? `, ${branchName}` : ""}`)} aria-haspopup="dialog" onClick={() => setBranch(true)}>
              <span className={s.lg}><GymGlyph /></span>
              <span className={s.gswText}>{branchName || gymName}{branchName ? <small>{gymName}</small> : null}</span>
              <Icon name="down" size={14} />
            </button>
            {solid && chrome.title ? <span className={s.ctitle} aria-hidden="true">{chrome.title}</span> : null}
            <span className={s.sp} />
            <button type="button" className={s.ib} aria-label={mobile.top.search} aria-haspopup="dialog" onClick={() => setSearch("all")}><Icon name="search" size={22} /></button>
            <button type="button" className={s.ib} aria-label={mobile.top.bell} aria-haspopup="dialog" onClick={() => setBell(true)}><Icon name="bell" size={22} /></button>
          </header>
        )}
        <main id="content" className={s.main} data-thread={onThread || undefined}>
          <Outlet />
        </main>
        {onThread ? null : (
          <TabBar label={mobile.tabs.label}>
            <TabBarItem as={Link} to="/home" label={mobile.tabs.home} icon="home" active={starts("/home")} />
            {canInbox ? <TabBarItem as={Link} to="/inbox" label={mobile.tabs.inbox} icon="chat" active={starts("/inbox")} badge={unread} /> : <span aria-hidden="true" />}
            <TabBarFab open={fab} onClick={() => setFab(true)} label={mobile.tabs.fab} />
            {slot ? <TabBarItem as={Link} to={slot.to} label={slot.label} icon={slot.icon} active={starts(slot.to)} /> : <span aria-hidden="true" />}
            <TabBarItem as={Link} to="/more" label={mobile.tabs.more} icon="grid" active={moreActive} />
          </TabBar>
        )}
        <FabSheet open={fab} onOpenChange={setFab} onSearchMembers={() => setSearch("members")} />
        <BranchSheet open={branch} onOpenChange={setBranch} />
        <BellSheet open={bell} onOpenChange={setBell} />
        <SearchScreen open={search !== false} membersOnly={search === "members"} onClose={() => setSearch(false)} />
      </div>
    </ChromeProvider>
  );
}
